import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { fmtNum } from "@/lib/utils"
import { getContentTypeColor } from "@/lib/content-types"
import { PageShell } from "@/components/page-shell"
import { Show, useUser } from "@clerk/react"
import { useConversations, useDocumentStats, useDocuments } from "@mimir/api-client"
import { format, formatDistanceToNow } from "date-fns"
import { ChevronRight } from "lucide-react"
import { CaptureUI } from "@/components/capture-form"
import { GridBackground } from "@/components/backgrounds/grid-bg"

export const Route = createFileRoute("/_app/home")({
  component: Home,
})

function Home() {
  const { user } = useUser()
  // Fetch only 7 docs for the recent list — no need to load everything
  const { data: docPages } = useDocuments({ limit: 7 })
  const { data: pinnedPages } = useDocuments({ pinned: true, limit: 5 })
  const { data: stats } = useDocumentStats()
  const { data: conversations } = useConversations()
  const navigate = useNavigate()

  const recentDocs = docPages?.pages[0]?.data ?? []
  const pinnedDocs = pinnedPages?.pages[0]?.data ?? []
  const totalDocuments = stats?.total
  const docsThisWeek = stats?.this_week
  const totalConversations = conversations?.length

  const lastCaptured = stats?.last_captured
    ? formatDistanceToNow(new Date(stats.last_captured), { addSuffix: true })
    : null

  const overview = [
    {
      title: "In Your Palace",
      value: fmtNum(totalDocuments),
      sub: "documents saved",
    },
    {
      title: "Added This Week",
      value: fmtNum(docsThisWeek),
      sub: "new captures",
    },
    {
      title: "Conversations",
      value: fmtNum(totalConversations),
      sub: "with Mimir",
    },
    {
      title: "Last Capture",
      value: lastCaptured ?? "—",
      sub: "most recent save",
    },
  ]

  const name = user?.firstName || user?.fullName || ""
  const hour = new Date().getHours()
  const greet =
    hour < 5
      ? "Still awake"
      : hour < 12
        ? "Good morning"
        : hour < 18
          ? "Good afternoon"
          : "Good evening"

  return (
    <>
      <GridBackground vignette={true} className="h-screen opacity-25! absolute z-[-1]" />
      <PageShell className="space-y-2">
        <Show when="signed-out">
          <p className="text-sm">Sign in to start.</p>
        </Show>
        <Show when="signed-in">
          <div className="flex flex-col gap-8">
            {/* Greeting */}
            <div>
              <h1 className="greeting">
                {greet}
                {name ? `, ${name}.` : "."} <em>What would you like to explore?</em>
              </h1>
              {docsThisWeek != null && docsThisWeek > 0 && (
                <p className="text-muted-foreground mt-2">
                  You've added {docsThisWeek} document{docsThisWeek !== 1 ? "s" : ""} this week.
                </p>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
              {overview.map((item) => (
                <Card key={item.title} className="py-0">
                  <CardHeader className="p-4 sm:p-5">
                    <CardDescription className="text-[10px] sm:text-xs tracking-wider font-mono uppercase leading-tight">
                      {item.title}
                    </CardDescription>
                    <CardTitle className="text-xl sm:text-2xl font-medium">{item.value}</CardTitle>
                    <p className="text-[10px] sm:text-xs text-muted-foreground/60 mt-0.5 leading-tight">
                      {item.sub}
                    </p>
                  </CardHeader>
                </Card>
              ))}
            </div>

            {/* Main content — always 2-col so capture panel doesn't blow up when empty */}
            <div className="grid grid-cols-1 lg:grid-cols-[3fr_1fr] gap-8 lg:gap-12 mt-2">
              {/* Recently Added */}
              <div className="flex flex-col min-w-0">
                <div className="flex items-center justify-between mb-4 gap-4">
                  <h2 className="text-xl sm:text-2xl font-bold shrink-0">Recently added</h2>
                  {totalDocuments != null &&
                    recentDocs.length > 0 &&
                    totalDocuments > recentDocs.length && (
                      <Link
                        to="/mind-palace"
                        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground whitespace-nowrap"
                      >
                        See all {totalDocuments} <ChevronRight className="w-4 h-4" />
                      </Link>
                    )}
                </div>
                <Separator />
                {recentDocs.length > 0 ? (
                  <div className="flex flex-col">
                    {recentDocs.map((doc) => (
                      <button
                        key={doc.id}
                        className="flex gap-3 sm:gap-5 py-4 sm:py-5 px-2 border-b border-border hover:bg-muted/50 transition-colors last:border-0 items-start text-left w-full"
                        onClick={() =>
                          navigate({
                            to: "/mind-palace/$documentId",
                            params: { documentId: doc.id },
                          })
                        }
                      >
                        <div className="text-center text-xs sm:text-sm text-muted-foreground font-mono shrink-0 w-10 sm:w-14 pt-0.5">
                          {format(new Date(doc.created_at), "MMM dd")}
                        </div>
                        <div className="flex flex-col gap-1 flex-1 min-w-0">
                          <span
                            className={`text-[10px] tracking-widest uppercase font-semibold ${getContentTypeColor(doc.content_type)}`}
                          >
                            {doc.content_type}
                          </span>
                          <h3
                            className="font-semibold text-base sm:text-lg leading-snug text-foreground line-clamp-2"
                            style={{ viewTransitionName: `doc-title-${doc.id}` }}
                          >
                            {doc.title || "Untitled Document"}
                          </h3>
                          {(doc.summary || doc.content_hint) && (
                            <p
                              className="text-xs sm:text-sm text-muted-foreground line-clamp-2 mt-0.5"
                              style={{ viewTransitionName: `doc-summary-${doc.id}` }}
                            >
                              {doc.summary || doc.content_hint}
                            </p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
                    <p className="text-muted-foreground text-sm">Nothing here yet.</p>
                    <p className="text-muted-foreground/60 text-xs max-w-[220px]">
                      Capture a link, paste some text, or upload a file to get started.
                    </p>
                  </div>
                )}
              </div>

              {/* Quick Capture */}
              <div className="flex flex-col gap-4 pt-3">
                <div className="flex flex-col gap-2">
                  <h2 className="text-lg sm:text-xl font-semibold">Quick Capture</h2>
                  <CaptureUI />
                </div>
                <div className="mt-2">
                  <h2 className="text-lg sm:text-xl font-semibold mb-3">Pinned</h2>
                  <div className="flex flex-col gap-2">
                    {pinnedDocs.length > 0 ? (
                      pinnedDocs.map((doc) => (
                        <button
                          key={doc.id}
                          className="p-3 rounded-xl border bg-card/50 hover:bg-muted/50 transition-colors text-left flex flex-col gap-1 group relative"
                          onClick={() =>
                            navigate({
                              to: "/mind-palace/$documentId",
                              params: { documentId: doc.id },
                            })
                          }
                        >
                          <span
                            className={`text-[9px] tracking-widest uppercase font-bold ${getContentTypeColor(doc.content_type)}`}
                          >
                            {doc.content_type}
                          </span>
                          <h4 className="text-sm font-semibold line-clamp-1 group-hover:text-primary transition-colors">
                            {doc.title || "Untitled"}
                          </h4>
                        </button>
                      ))
                    ) : (
                      <p className="text-xs text-muted-foreground italic px-1">No pinned items.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Show>
      </PageShell>
    </>
  )
}
